import { Suspense } from "react";
import { Lock } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { usePermissions } from "@/core/auth";
import { ErrorBoundary } from "@/core/components";
import { Button, Carte, Vide } from "@/shared/ui";
import type { PanelInstance } from "@/shell/layout";
import {
  panelRegistry,
  resolvePanelView,
  type PanelId,
} from "@/shell/PanelRegistry";

interface PanelHostProps {
  instance: PanelInstance;
}

/**
 * PanelHost : resout panelId → def → view → component, et le rend avec params.
 * Wrap le rendering dans un ErrorBoundary par-instance pour qu'un crash
 * n'arrete que ce panel, pas le shell entier.
 *
 * Si le panelId n'est pas dans le registry (layout cache avec un ancien ID),
 * on retombe silencieusement sur "empty" pour ne pas casser l'UI.
 *
 * Permission gate : si le panel a un `requiredPermission` et que l'user ne
 * l'a pas (cas typique : workspace persisté avec un panel auquel l'user a
 * perdu accès), on affiche un Access denied au lieu du component.
 */
export function PanelHost({ instance }: PanelHostProps) {
  const { t } = useTranslation();
  const { permissions, isLoading: permsLoading } = usePermissions();
  const def =
    panelRegistry[instance.panelId as PanelId] ?? panelRegistry["empty"];
  const localizedTitle = t(`panels.registry.${def.id}.title`);

  const view = resolvePanelView(def, instance.viewId);
  const Component = view.component;

  // Permission gate : si le panel demande une perm et que l'user ne l'a pas
  // → Access denied. Pendant le bootstrap (permsLoading), on ne render pas
  // pour éviter un flash du contenu protégé.
  if (def.requiredPermission) {
    if (permsLoading) {
      return <div className="h-full" />;
    }
    if (!permissions?.has(def.requiredPermission)) {
      return (
        <div className="flex h-full items-center justify-center p-4">
          <Vide
            icone={<Lock className="h-6 w-6" />}
            titre={t("common.accessDenied", { defaultValue: "Access denied" })}
            texte={t("common.accessDeniedBody", {
              defaultValue:
                "You don't have permission to view this panel. Ask an admin to update your role.",
            })}
          />
        </div>
      );
    }
  }

  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <div className="flex h-full items-center justify-center p-4">
          <Vide
            titre={<span className="text-erreur">{t("common.errorIn", { name: localizedTitle })}</span>}
            texte={error.message}
            action={
              <Button variant="secondary" size="md" onClick={reset}>
                {t("common.retry")}
              </Button>
            }
          />
        </div>
      )}
    >
      <Suspense fallback={<PanelLoadingFallback />}>
        <Component
          instanceId={instance.id}
          params={instance.params as Record<string, unknown> | undefined}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

/** Le panneau qui charge : une carte de verre qui respire, à la place du bloc gris. */
function PanelLoadingFallback() {
  return (
    <div className="h-full min-h-0 w-full p-3">
      <Carte remplissage="aucun" className="h-full animate-pulse" aria-busy />
    </div>
  );
}
